/**
 * BookClubRooms.jsx — 3 default rooms (General · Book of Week · Audio of Week).
 * Lives at /club. Companion to the existing per-author BookClub.jsx
 * at /book-club. Spec Feb 26 2026.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { MessageSquare, Send, Loader2, ArrowLeft, BookMarked, Headphones, Users as UsersIcon } from 'lucide-react';
import { toast } from 'sonner';
import { authFetch, checkAuth } from '../utils/authUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ROOM_ICONS = {
  general: UsersIcon,
  book_of_the_week: BookMarked,
  audio_of_the_week: Headphones,
};
const ROOM_TINTS = {
  general: 'from-indigo-500/15 border-indigo-400/40',
  book_of_the_week: 'from-amber-500/15 border-amber-400/40',
  audio_of_the_week: 'from-emerald-500/15 border-emerald-400/40',
};

const formatTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return d.toLocaleDateString();
};

const Avatar = ({ user }) => (
  user?.picture ? (
    <img src={user.picture} alt="" className="w-8 h-8 rounded-full object-cover" />
  ) : (
    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600" />
  )
);

const BookClubRooms = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialRoom = searchParams.get('room');
  const [me, setMe] = useState(null);
  const [rooms, setRooms] = useState([]);
  const [activeRoom, setActiveRoom] = useState(initialRoom);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const listEndRef = useRef(null);

  useEffect(() => { (async () => { setMe(await checkAuth()); })(); }, []);

  const fetchRooms = useCallback(async () => {
    try {
      const r = await authFetch(`${BACKEND_URL}/api/bookclub/rooms`);
      if (r.ok) {
        const d = await r.json();
        setRooms(d.rooms || []);
      }
    } catch (_) {}
  }, []);

  const fetchMessages = useCallback(async (room_id) => {
    if (!room_id) return;
    setLoadingMessages(true);
    try {
      const r = await authFetch(`${BACKEND_URL}/api/bookclub/rooms/${room_id}`);
      const d = await r.json();
      if (r.ok) setMessages(d.messages || []);
    } finally { setLoadingMessages(false); }
  }, []);

  useEffect(() => { fetchRooms(); }, [fetchRooms]);
  useEffect(() => { if (activeRoom) fetchMessages(activeRoom); }, [activeRoom, fetchMessages]);
  useEffect(() => { listEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const openRoom = (room_id) => {
    setActiveRoom(room_id);
    setSearchParams({ room: room_id }, { replace: true });
  };
  const closeRoom = () => {
    setActiveRoom(null);
    setSearchParams({}, { replace: true });
  };

  const send = async () => {
    const text = draft.trim();
    if (!text || !activeRoom) return;
    setSending(true);
    try {
      const r = await authFetch(`${BACKEND_URL}/api/bookclub/rooms/${activeRoom}/post`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ body: text }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || `Send failed (${r.status})`);
      setMessages((prev) => [...prev, d.message]);
      setDraft('');
      fetchRooms();
    } catch (err) {
      toast.error(err.message || 'Could not send');
    } finally { setSending(false); }
  };

  return (
    <main className="max-w-3xl mx-auto px-4 py-6" data-testid="bookclub-rooms-page">
      {!activeRoom ? (
        <>
          <h1 className="text-2xl sm:text-3xl font-bold text-white mb-2 flex items-center gap-2">
            <MessageSquare className="w-6 h-6 text-pink-400" /> Book Club
          </h1>
          <p className="text-sm text-slate-400 mb-5">Three rooms, one community. Jump in.</p>
          <div className="space-y-3" data-testid="bookclub-rooms-list">
            {rooms.map((r) => {
              const Icon = ROOM_ICONS[r.room_id] || MessageSquare;
              const tint = ROOM_TINTS[r.room_id] || ROOM_TINTS.general;
              return (
                <button
                  key={r.room_id}
                  type="button"
                  onClick={() => openRoom(r.room_id)}
                  className={`w-full text-left p-4 rounded-xl bg-gradient-to-br ${tint} border hover:brightness-110 transition-all`}
                  data-testid={`bookclub-room-${r.room_id}`}
                >
                  <div className="flex items-center gap-3">
                    <Icon className="w-6 h-6 text-white flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-lg font-bold text-white">{r.title}</div>
                      {r.featured ? (
                        <div className="text-xs text-amber-200 truncate mt-0.5" data-testid={`bookclub-featured-${r.room_id}`}>
                          📌 Featured: <span className="font-semibold">{r.featured.title}</span>
                          {r.featured.author_name ? ` · ${r.featured.author_name}` : ''}
                        </div>
                      ) : null}
                      <div className="text-xs text-slate-300 truncate">
                        {r.last_message_preview || r.subtitle}
                      </div>
                      <div className="text-[10px] text-slate-400 mt-0.5">
                        {r.last_message_at ? formatTime(r.last_message_at) : 'No messages yet'}
                      </div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </>
      ) : (
        <section className="rounded-xl bg-slate-800/50 border border-slate-700 flex flex-col min-h-[60vh] max-h-[80vh]" data-testid="bookclub-conversation">
          <header className="flex items-center gap-2 px-3 py-2.5 border-b border-slate-700">
            <button type="button" onClick={closeRoom} className="p-1 rounded hover:bg-slate-700" aria-label="Back">
              <ArrowLeft className="w-4 h-4 text-slate-300" />
            </button>
            <div className="flex-1">
              <div className="text-sm font-bold text-white">{rooms.find((r) => r.room_id === activeRoom)?.title}</div>
              <div className="text-[10px] text-slate-400">{rooms.find((r) => r.room_id === activeRoom)?.subtitle}</div>
            </div>
          </header>
          <div className="flex-1 overflow-y-auto px-3 py-3 space-y-2" data-testid="bookclub-messages">
            {loadingMessages && <div className="text-center text-slate-400 text-sm py-4"><Loader2 className="w-4 h-4 inline-block animate-spin mr-2" /> Loading…</div>}
            {!loadingMessages && messages.length === 0 && <div className="text-center text-sm text-slate-500 py-8">Be the first to start the conversation.</div>}
            {messages.map((m) => {
              const mine = m.user_id === me?.user_id;
              return (
                <div key={m.message_id} className={`flex ${mine ? 'justify-end' : 'justify-start'} items-end gap-2`}>
                  {!mine && <Avatar user={m.author} />}
                  <div className={`max-w-[75%] rounded-2xl px-3 py-2 text-sm ${mine ? 'bg-indigo-600 text-white' : 'bg-slate-700 text-slate-100'}`} data-testid={`bookclub-msg-${m.message_id}`}>
                    {!mine && <div className="text-[10px] uppercase font-bold text-indigo-300 mb-0.5">{m.author?.name}</div>}
                    <div className="whitespace-pre-wrap break-words">{m.body}</div>
                    <div className={`text-[10px] mt-1 ${mine ? 'text-indigo-200/80' : 'text-slate-400'}`}>{formatTime(m.created_at)}</div>
                  </div>
                </div>
              );
            })}
            <div ref={listEndRef} />
          </div>
          <div className="border-t border-slate-700 p-2 flex items-end gap-2">
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value.slice(0, 2000))}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
              rows={2}
              placeholder="Write a message…"
              className="flex-1 rounded-lg bg-slate-900 border border-slate-700 text-white text-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500/50 resize-none"
              data-testid="bookclub-composer-input"
            />
            <button type="button" onClick={send} disabled={sending || !draft.trim()} className="p-2.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 disabled:bg-slate-600 text-white" data-testid="bookclub-send-btn">
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </button>
          </div>
        </section>
      )}
    </main>
  );
};

export default BookClubRooms;
